import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { Box, Grid2, Stack, Typography, useTheme } from "@mui/material";
import { EGInput } from "../../../../MainComponents/Input/EGInput";
import { EGBasicSelect } from "../../../../MainComponents/Select/EGBasicSelect";
import {
  InvestigationButton,
  InvestigationCollapsable,
  InvestigationItemContainer,
  Separator,
} from "../components";
import {
  ACCOUNT_FLOWS,
  FINANCIAL_IMPACT_CURRENCIES,
  getAccountsTotal,
  getNetLoss,
  normalizeFinancialImpact,
} from "../../../../../helpers/financialImpact";
import { AddAccount } from "./Modals/AddAccount";
import { AccountDetails, AccountTitle } from "./AccountSummary";
import { AmountField, AmountRow, useFlowColor } from "./components";

export const AddFinancialImpact = ({
  financialImpact,
  setIsEditing,
  handleSave,
  isLoading,
}) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();
  const flowColor = useFlowColor();

  const [impact, setImpact] = useState(() =>
    normalizeFinancialImpact(financialImpact)
  );
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);

  useEffect(() => {
    setImpact(normalizeFinancialImpact(financialImpact));
  }, [financialImpact]);

  const handleChange = (field) => (value) =>
    setImpact((prev) => ({ ...prev, [field]: value }));

  const setAccounts = (accounts) => handleChange("accounts")(accounts);

  const handleEditAccount = (idx) => {
    setSelectedAccount(idx);
    setShowAddAccount(true);
  };

  const handleDeleteAccount = (idx) => {
    const newAccounts = _.cloneDeep(impact.accounts);
    newAccounts.splice(idx, 1);
    setAccounts(newAccounts);
  };

  const currencyOptions = FINANCIAL_IMPACT_CURRENCIES.map((code) => ({
    label: code,
    value: code,
  }));

  const affectedTotal = getAccountsTotal(
    impact.accounts,
    ACCOUNT_FLOWS.AFFECTED
  );
  const beneficiaryTotal = getAccountsTotal(
    impact.accounts,
    ACCOUNT_FLOWS.BENEFICIARY
  );

  return (
    <>
      <AddAccount
        show={showAddAccount}
        onHide={() => setShowAddAccount(false)}
        accounts={impact.accounts}
        setAccounts={setAccounts}
        selectedAccount={selectedAccount}
        setSelectedAccount={setSelectedAccount}
        currency={impact.currency}
      />

      <Box padding={2}>
        <Separator text={t("financialImpact.amounts")} />

        <Typography fontSize={13} color={theme.palette.blueGray500.main} mt={1}>
          {t("financialImpact.amounts_description")}
        </Typography>

        <Grid2 container spacing={2} mt={1}>
          <Grid2 size={{ xs: 12, sm: 6, lg: 3 }}>
            <EGBasicSelect
              label={t("financialImpact.currency")}
              value={impact.currency}
              options={currencyOptions}
              onChange={(e) => handleChange("currency")(e.target.value)}
              isValid={null}
              showHelpText={false}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6, lg: 3 }}>
            <AmountField
              label={t("financialImpact.potential_amount")}
              value={impact.potential_amount}
              onChange={handleChange("potential_amount")}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6, lg: 3 }}>
            <AmountField
              label={t("financialImpact.final_amount")}
              value={impact.final_amount}
              onChange={handleChange("final_amount")}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6, lg: 3 }}>
            <AmountField
              label={t("financialImpact.recovered_amount")}
              value={impact.recovered_amount}
              onChange={handleChange("recovered_amount")}
            />
          </Grid2>

          <Grid2 size={12}>
            <AmountRow
              label={t("financialImpact.net_loss")}
              amount={getNetLoss(impact)}
              currency={impact.currency}
              color={theme.palette.red.main}
            />
          </Grid2>
        </Grid2>

        <Separator text={t("financialImpact.money_trail")} sx={{ mt: 3 }} />

        <Typography fontSize={13} color={theme.palette.blueGray500.main} mt={1}>
          {t("financialImpact.money_trail_description")}
        </Typography>

        <Grid2 container spacing={2} mt={1}>
          <Grid2 size={{ xs: 12, lg: 6 }}>
            <EGInput
              label={t("financialImpact.origin")}
              value={impact.origin}
              onChange={(e) => handleChange("origin")(e.target.value)}
              multiline
              rows={4}
              showHelpText={false}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, lg: 6 }}>
            <EGInput
              label={t("financialImpact.destination")}
              value={impact.destination}
              onChange={(e) => handleChange("destination")(e.target.value)}
              multiline
              rows={4}
              showHelpText={false}
            />
          </Grid2>
        </Grid2>

        {/* Cuentas: se capturan a mano, una por una */}
        <Stack
          spacing={2}
          mt={3}
          p={2}
          borderRadius="5px"
          border={`1px solid ${theme.palette.primary.main}`}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <InvestigationItemContainer
              color={theme.palette.primary.main}
              width="fit-content"
              sx={{ padding: 0.8 }}
            >
              <Typography fontSize={13} fontWeight={600}>
                {t("financialImpact.accounts")}
              </Typography>
            </InvestigationItemContainer>

            <InvestigationButton
              label={t("financialImpact.add_account")}
              size="medium"
              icon={false}
              onClick={() => setShowAddAccount(true)}
            />
          </Stack>

          {impact.accounts.length === 0 ? (
            <Typography fontSize={13} color={theme.palette.blueGray500.main}>
              {t("financialImpact.no_accounts_loaded")}
            </Typography>
          ) : (
            <>
              <hr style={{ margin: 0 }} />

              <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <AmountRow
                    label={t("financialImpact.affected_total")}
                    amount={affectedTotal}
                    currency={impact.currency}
                    color={flowColor(ACCOUNT_FLOWS.AFFECTED)}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <AmountRow
                    label={t("financialImpact.beneficiary_total")}
                    amount={beneficiaryTotal}
                    currency={impact.currency}
                    color={flowColor(ACCOUNT_FLOWS.BENEFICIARY)}
                  />
                </Grid2>
              </Grid2>

              <Stack spacing={1}>
                {impact.accounts.map((account, idx) => (
                  <InvestigationCollapsable
                    key={`account-${idx}`}
                    title={
                      <AccountTitle
                        account={account}
                        currency={impact.currency}
                      />
                    }
                    details={<AccountDetails account={account} />}
                    buttons={[
                      {
                        tooltip: t("financialImpact.delete"),
                        name: "trash_can",
                        color: theme.palette.red.main,
                        onClick: () => handleDeleteAccount(idx),
                      },
                      {
                        tooltip: t("financialImpact.edit"),
                        name: "outline_edit",
                        color: theme.palette.primary.main,
                        onClick: () => handleEditAccount(idx),
                      },
                    ]}
                  />
                ))}
              </Stack>
            </>
          )}
        </Stack>

        <Grid2 container spacing={2} mt={3}>
          <Grid2 size={12}>
            <EGInput
              label={t("financialImpact.notes")}
              value={impact.notes}
              onChange={(e) => handleChange("notes")(e.target.value)}
              multiline
              rows={3}
              showHelpText={false}
            />
          </Grid2>
        </Grid2>

        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="flex-end"
          spacing={1}
          mt={2}
        >
          <InvestigationButton
            label={t("financialImpact.save")}
            variant="success"
            size="medium"
            icon="mark_check_bold"
            onClick={() => handleSave(impact)}
            loading={isLoading}
          />
          <InvestigationButton
            label={t("financialImpact.cancel")}
            icon="close_icon"
            size="medium"
            onClick={() => setIsEditing(false)}
            loading={isLoading}
          />
        </Stack>
      </Box>
    </>
  );
};
